import { useTable } from "@refinedev/core";
import { Link, useSearchParams } from "react-router";

import { Badge } from "@/components/Badge";
import {
  ErrorBox,
  FilterBar,
  Loading,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
} from "@/components/Page";
import { EmptyRow, Sub, Table, Td, Th, Tr } from "@/components/Table";
import {
  ANSWERED_VIA_OPTIONS,
  STORY_STATUS_OPTIONS,
  answeredViaLabel,
  formatDateTime,
  storyStatus,
  transcriptStatus,
} from "@/lib/format";
import { filterValue } from "@/lib/query";

export interface StoryRow extends Record<string, unknown> {
  id: string;
  memoir_id: string;
  memoir_title: string | null;
  status: string;
  answered_via: string | null;
  category: string | null;
  category_name: string | null;
  question: string | null;
  published_at: string | null;
  created_at: string;
  sensitive: boolean;
  hidden_in_book: boolean;
  photos_count: number;
  recordings_count: number;
  versions_count: number;
  transcript_status: string | null;
}

export function StoriesListPage() {
  const [searchParams] = useSearchParams();
  const opowiescZAdresu = searchParams.get("memoir_id") ?? "";

  const { result, tableQuery, filters, setFilters, currentPage, setCurrentPage, pageSize } =
    useTable<StoryRow>({
      resource: "stories",
      sorters: { initial: [{ field: "published_at", order: "desc" }] },
      filters: {
        initial: opowiescZAdresu
          ? [{ field: "memoir_id", operator: "eq", value: opowiescZAdresu }]
          : undefined,
      },
    });

  const ustawFiltr = (field: string, value: string) =>
    setFilters([{ field, operator: "eq", value }], "merge");

  return (
    <>
      <PageHeader
        title="Historie"
        description="Przegląd po dacie i kategorii. Widać tu wyłącznie metadane - treść i tytuł są za osobnym uprawnieniem, bo to cudze wspomnienia, a nie nasz materiał roboczy."
      />

      <FilterBar>
        <Select
          label="Status"
          value={filterValue(filters, "status")}
          options={STORY_STATUS_OPTIONS}
          onChange={(value) => ustawFiltr("status", value)}
        />
        <Select
          label="Kanał odpowiedzi"
          value={filterValue(filters, "answered_via")}
          options={ANSWERED_VIA_OPTIONS}
          onChange={(value) => ustawFiltr("answered_via", value)}
        />
        <SearchInput
          label="Kategoria"
          placeholder="np. childhood_home"
          value={filterValue(filters, "category")}
          onChange={(value) => ustawFiltr("category", value)}
        />
        <label className="flex flex-col gap-1 text-base text-ink-soft">
          Opublikowane od
          <input
            type="date"
            value={filterValue(filters, "published_from").slice(0, 10)}
            onChange={(event) =>
              ustawFiltr(
                "published_from",
                event.target.value ? `${event.target.value}T00:00:00Z` : "",
              )
            }
            className="rounded-card border border-line bg-surface px-3 py-2 text-ink"
          />
        </label>
        <label className="flex flex-col gap-1 text-base text-ink-soft">
          do
          <input
            type="date"
            value={filterValue(filters, "published_to").slice(0, 10)}
            onChange={(event) =>
              ustawFiltr("published_to", event.target.value ? `${event.target.value}T23:59:59Z` : "")
            }
            className="rounded-card border border-line bg-surface px-3 py-2 text-ink"
          />
        </label>
      </FilterBar>

      <ErrorBox error={tableQuery.error} />

      {tableQuery.isLoading ? (
        <Loading />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Opublikowana</Th>
                <Th>Opowieść</Th>
                <Th>Pytanie</Th>
                <Th>Kategoria</Th>
                <Th>Skąd</Th>
                <Th>Status</Th>
                <Th align="right">Zdjęcia</Th>
                <Th>Transkrypcja</Th>
              </tr>
            </thead>
            <tbody>
              {result.data.length === 0 && (
                <EmptyRow colSpan={8}>Żadna historia nie pasuje do tych filtrów.</EmptyRow>
              )}
              {result.data.map((story) => {
                const status = storyStatus(story.status);
                const transkrypcja = story.transcript_status
                  ? transcriptStatus(story.transcript_status)
                  : null;
                return (
                  <Tr key={story.id}>
                    <Td nowrap>
                      <Link
                        to={`/historie/${story.id}`}
                        className="text-accent underline underline-offset-4 hover:text-accent-dark"
                      >
                        {formatDateTime(story.published_at ?? story.created_at)}
                      </Link>
                    </Td>
                    <Td nowrap>{story.memoir_title ?? "-"}</Td>
                    <Td>
                      {story.question ?? "-"}
                      {story.sensitive && <Sub>temat delikatny</Sub>}
                    </Td>
                    <Td nowrap>{story.category_name ?? "-"}</Td>
                    <Td nowrap>{answeredViaLabel(story.answered_via)}</Td>
                    <Td>
                      <Badge tone={status.tone}>{status.label}</Badge>
                    </Td>
                    <Td align="right" numeric>
                      {story.photos_count}
                    </Td>
                    <Td>
                      {transkrypcja ? (
                        <Badge tone={transkrypcja.tone}>{transkrypcja.label}</Badge>
                      ) : (
                        <span className="text-xs text-ink-soft">-</span>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>

          <Pagination
            page={currentPage}
            pageSize={pageSize}
            total={result.total ?? 0}
            onChange={setCurrentPage}
          />
        </>
      )}
    </>
  );
}
